import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "channels container py-4" }
const _hoisted_2 = {
  key: 0,
  class: "alert alert-danger alert-dismissible fade show"
}
const _hoisted_3 = {
  key: 1,
  class: "text-center py-5"
}
const _hoisted_4 = {
  key: 2,
  class: "mt-2"
}
const _hoisted_5 = {
  key: 4,
  class: "modal show d-block",
  tabindex: "-1",
  style: {"background-color":"rgba(0, 0, 0, 0.5)"}
}
const _hoisted_6 = { class: "modal-dialog modal-dialog-centered" }
const _hoisted_7 = { class: "modal-content" }
const _hoisted_8 = { class: "modal-body" }
const _hoisted_9 = {
  key: 0,
  class: "alert alert-danger mt-3 mb-0 d-flex align-items-center"
}
const _hoisted_10 = { class: "modal-footer" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = ["disabled"]
const _hoisted_13 = {
  key: 0,
  class: "spinner-border spinner-border-sm me-2"
}

import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useChannelsStore } from "@/stores/telegramChannels";
import type { TelegramChannel, ChannelType } from "@/stores/telegramChannels"; // ChannelType will be needed
import ChannelFormModal from "@/components/channels/ChannelFormModal.vue";
import ServiceChannelsTable from "@/components/channels/ServiceChannelsTable.vue";
import NetworkingChannelsTable from "@/components/channels/NetworkingChannelsTable.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'Channels',
  setup(__props) {

const channelsStore = useChannelsStore();
const store = useStore();

// State
const showModal = ref(false);
const showDeleteModal = ref(false);
const editingChannel = ref<TelegramChannel | null>(null);
const deletingChannel = ref<TelegramChannel | null>(null);
const createType = ref<ChannelType>("service"); // Default
const saveError = ref<string | null>(null);
const deleteError = ref<string | null>(null);

// Computed
const channels = computed(() => channelsStore.channels);

const serviceChannels = computed(() =>
  channels.value.filter((c) => c.type !== "networkMarketing")
);
const networkingChannels = computed(() =>
  channels.value.filter((c) => c.type === "networkMarketing")
);
const loading = computed(() => channelsStore.loading);
const error = computed(() => channelsStore.error);
const isEditing = computed(() => editingChannel.value !== null);

// Methods
const loadChannels = async () => {
  try {
    await channelsStore.fetchChannels();
    store.dispatch("ranks/doGetRanksInfo");
  } catch (error) {
    console.error("Error loading channels:", error);
  }
};

const openCreateModal = (type: ChannelType) => {
  editingChannel.value = null;
  createType.value = type;
  showModal.value = true;
  saveError.value = null;
};

const openEditModal = (channel: TelegramChannel) => {
  editingChannel.value = { ...channel };
  // Set createType based on channel type for fallback or logical consistency,
  // though the modal uses valid channel data if provided.
  createType.value = channel.type || "service";
  showModal.value = true;
  saveError.value = null;
};

const closeModal = () => {
  showModal.value = false;
  editingChannel.value = null;
};

const handleSave = async (channel: TelegramChannel) => {
  try {
    if (isEditing.value) {
      await channelsStore.updateChannel(channel.id, channel);
    } else {
      await channelsStore.createChannel(channel);
    }
    closeModal();
  } catch (error: any) {
    console.error("Error saving channel:", error);
    saveError.value = error.message || "Error saving channel";
    channelsStore.clearError(); // Clear global error to avoid double alert
  }
};

const confirmDelete = (channel: TelegramChannel) => {
  deletingChannel.value = channel;
  showDeleteModal.value = true;
  deleteError.value = null;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  deletingChannel.value = null;
};

const handleDelete = async () => {
  if (!deletingChannel.value) return;

  try {
    await channelsStore.deleteChannel(deletingChannel.value.id);
    closeDeleteModal();
  } catch (error: any) {
    console.error("Error deleting channel:", error);
    deleteError.value = error.message || "Error deleting channel";
    channelsStore.clearError(); // Clear global error
  }
};

const handleUpdateOrder = async (
  updates: { channelId: string; priority: number }[]
) => {
  try {
    await channelsStore.updatePriorities(updates);
    // Success: pinia store handles local update and sorting
  } catch (error: any) {
    console.error("Error updating channel priorities:", error);
    // Store handles error state, component computes it
  }
};

const clearError = () => {
  channelsStore.clearError();
};

// Lifecycle
onMounted(() => {
  loadChannels();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-exclamation-triangle me-2" }, null, -1)),
          _createTextVNode(" " + _toDisplayString(error.value) + " ", 1),
          _createElementVNode("button", {
            type: "button",
            class: "btn-close",
            onClick: clearError,
            "aria-label": "Close"
          })
        ]))
      : _createCommentVNode("", true),
    (loading.value && channels.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[3] || (_cache[3] = [
          _createElementVNode("div", {
            class: "spinner-border text-primary",
            role: "status"
          }, [
            _createElementVNode("span", { class: "visually-hidden" }, "Cargando...")
          ], -1),
          _createElementVNode("p", { class: "mt-3 text-muted" }, "Cargando canales...", -1)
        ]))]))
      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(ServiceChannelsTable, {
            channels: serviceChannels.value,
            loading: loading.value,
            onCreate: _cache[0] || (_cache[0] = ($event: any) => (openCreateModal('service'))),
            onEdit: openEditModal,
            onDelete: confirmDelete,
            onRefresh: loadChannels,
            onUpdateOrder: handleUpdateOrder
          }, null, 8, ["channels", "loading"]),
          _createVNode(NetworkingChannelsTable, {
            channels: networkingChannels.value,
            loading: loading.value,
            onCreate: _cache[1] || (_cache[1] = ($event: any) => (openCreateModal('networkMarketing'))),
            onEdit: openEditModal,
            onDelete: confirmDelete,
            onRefresh: loadChannels,
            onUpdateOrder: handleUpdateOrder
          }, null, 8, ["channels", "loading"])
        ])),
    (showModal.value)
      ? (_openBlock(), _createBlock(ChannelFormModal, {
          key: 3,
          channel: editingChannel.value,
          "is-editing": isEditing.value,
          "initial-type": createType.value,
          "api-error": saveError.value,
          loading: loading.value,
          onSave: handleSave,
          onClose: closeModal
        }, null, 8, ["channel", "is-editing", "initial-type", "api-error", "loading"]))
      : _createCommentVNode("", true),
    (showDeleteModal.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", { class: "modal-header" }, [
                _cache[4] || (_cache[4] = _createElementVNode("h5", { class: "modal-title" }, [
                  _createElementVNode("i", { class: "fas fa-exclamation-triangle text-warning me-2" }),
                  _createTextVNode(" Confirmar Eliminación ")
                ], -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn-close",
                  onClick: closeDeleteModal
                })
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("p", null, [
                  _cache[5] || (_cache[5] = _createTextVNode(" ¿Estás seguro de que deseas eliminar el canal ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(deletingChannel.value?.name), 1),
                  _cache[6] || (_cache[6] = _createTextVNode("? ", -1))
                ]),
                _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-danger mb-0" }, [
                  _createElementVNode("i", { class: "fas fa-exclamation-circle me-1" }),
                  _createTextVNode(" Esta acción no se puede deshacer. ")
                ], -1)),
                (deleteError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _cache[8] || (_cache[8] = _createElementVNode("i", { class: "fas fa-exclamation-triangle me-2" }, null, -1)),
                      _createElementVNode("div", null, [
                        _cache[7] || (_cache[7] = _createElementVNode("strong", null, "Error:", -1)),
                        _createTextVNode(" " + _toDisplayString(deleteError.value), 1)
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-secondary",
                  onClick: closeDeleteModal,
                  disabled: loading.value
                }, " Cancelar ", 8, _hoisted_11),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-danger",
                  onClick: handleDelete,
                  disabled: loading.value
                }, [
                  (loading.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_13))
                    : _createCommentVNode("", true),
                  _cache[10] || (_cache[10] = _createTextVNode(" Eliminar ", -1))
                ], 8, _hoisted_12)
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})